// ContractPreview.js
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useFormInput, dateFormatDateTime, comma } from "@/utils";
import { Loading, Toast, Confirm } from "@/components";

import {
    originOptions,
    transmissionOptions,
    vehicleStatusOptions,
    insuranceAgeOptions,
    contractMonthOptions,
    emergencyServiceOptions,
    daeinPay1Options,
    daeinPay2Options,
    daemulPayOptions,
    jasonOptions,
    selfCarDamageTypeOptions,
    selfCarDamageAmountOptions,
    selfCarDamageRateOptions,
    noInsInjuryOptions,
    capitalCorpOptions,
    driverTypeOptions,
} from "@/constants/selectOptions";

import signSample from "@/assets/images/sign_sample.png";

// RTK Query APIs
import {
    useSignContractPdfMutation,
    useGetContractGroupByIdQuery,
    useGetContractGroupContractByIdQuery,
    useSendContractMutation,
} from "@/redux/contractApi";
import { useUploadFileMutation } from "@/redux/uploadApi";

export const ContractPreview = () => {
    const { contractGroupId, contractId } = useParams();
    const reportRef = useRef(null);

    const { formData, handleChange, setFormData } = useFormInput({
        pdfMode: false,
        toastMsg: "",
        isLoading: false,
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        signLinkGuidePopupStatus: false,
        // 상세 응답 데이터
        contract: {},
    });
    const { signLinkGuidePopupStatus } = formData;

    // 1) 계약서 상세조회 API
    const {
        data: contractData,
        isLoading: isDetailLoading,
        isError: isDetailError,
        error: detailError,
    } = useGetContractGroupContractByIdQuery(
        { contractGroupId, contractId },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    // 2) 전자서명 발송 API
    const [signContractPdf] = useSignContractPdfMutation();
    const [sendContract] = useSendContractMutation();
    const [uploadFile] = useUploadFileMutation();

    // ===== 계약 상세조회 =====
    useEffect(() => {
        if (contractData && contractData.data) {
            const detail = contractData.data;
            setFormData((prev) => ({
                ...prev,
                contract: detail,
            }));

            if (detail.authAbleYn === "N") {
                setFormData((prev) => ({
                    ...prev,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "알림",
                        confirmDescription: "본인인증이 진행되지 않은 고객입니다. 확인후 출고 하시기 바랍니다.",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () =>
                                    setFormData((prevState) => ({
                                        ...prevState,
                                        confirmStatus: { confirmPopupStatus: false },
                                    })),
                            },
                        ],
                    },
                }));
            }
        }
    }, [contractData, setFormData]);

    // 일반 다운로드 핸들러 (기존)
    const handlePdfDownload = async () => {
        try {
            setFormData((prev) => ({ ...prev, pdfMode: true, isLoading: true }));

            const pdf = new jsPDF("p", "pt", "a4");
            const pages = document.querySelectorAll(".page");

            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], { scale: 1.2 });
                const imgData = canvas.toDataURL("image/png");
                const imgProps = pdf.getImageProperties(imgData);

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            }

            pdf.save(`contract-${Date.now()}.pdf`);
        } catch (error) {
            console.error("PDF 생성 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "PDF 생성 실패",
            }));
        } finally {
            setFormData((prev) => ({ ...prev, pdfMode: false, isLoading: false }));
        }
    };

    // 전자서명 발송 버튼 → 팝업 열기 전, pdfFile 세팅
    const handleSendContractPopup = async () => {
        try {
            // 1) PDF를 메모리에서 생성 (blob) -> formData.pdfFile 에 저장
            setFormData((prev) => ({ ...prev, pdfMode: true, isLoading: true }));

            const pdf = new jsPDF("p", "pt", "a4");
            const pages = document.querySelectorAll(".page");

            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], { scale: 1.2 });
                const imgData = canvas.toDataURL("image/png");
                const imgProps = pdf.getImageProperties(imgData);

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            }

            // pdf.output("blob") 로 Blob 객체 생성
            const pdfBlob = pdf.output("blob");

            // 2) formData에 pdfFile로 저장
            setFormData((prev) => ({
                ...prev,
                pdfMode: false,
                isLoading: false,
                // Blob 객체를 File로 변환할 수도 있지만, blob만으로도 FormData.append가 가능.
                pdfFile: new File([pdfBlob], `contract_${Date.now()}.pdf`, { type: "application/pdf" }),
                // 팝업 열기
                signLinkGuidePopupStatus: true,
            }));
        } catch (error) {
            console.error("PDF 생성 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "PDF 생성 실패",
                pdfMode: false,
                isLoading: false,
            }));
        }
    };

    // 최종계약서 다운로드 버튼 클릭 이벤트
    const handleFinalContractDownload = async () => {
        try {
            setFormData((prev) => ({ ...prev, pdfMode: true, isLoading: true }));

            // 1) 화면에 보이는 .page 요소들 캡처하여 PDF 생성
            const pdf = new jsPDF("p", "pt", "a4");
            const pages = document.querySelectorAll(".page");

            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], { scale: 1.2, useCORS: true, allowTaint: false });
                const imgData = canvas.toDataURL("image/png");
                const imgProps = pdf.getImageProperties(imgData);

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            }

            // 2) PDF -> Blob
            const pdfBlob = pdf.output("blob");
            const finalFile = new File([pdfBlob], `final_contract_${Date.now()}.pdf`, {
                type: "application/pdf",
            });

            // 3) 업로드 시 FormData에 담아서 전달
            const formDataObj = new FormData();
            formDataObj.append("file", finalFile);

            // 4) 서버 업로드 (uploadFile API)
            const uploadResp = await uploadFile(formDataObj).unwrap();
            const fileId = uploadResp.data?.id;
            if (!fileId) {
                throw new Error("업로드 실패: fileId가 없습니다.");
            }

            // 5) signContractPdf API 호출
            const result = await signContractPdf({
                contractGroupId,
                contractId,
                body: {
                    signContractUploadId: fileId,
                },
            }).unwrap();

            // 6) 최종적으로 result.data.objectUrl을 이용하여 파일 다운로드 진행
            if (result.data && result.data.objectUrl) {
                const link = document.createElement("a");
                link.href = result.data.objectUrl;
                link.download = `final_contract_${Date.now()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            setFormData((prev) => ({
                ...prev,
                toastMsg: "최종 계약서가 업로드 및 다운로드 되었습니다.",
            }));
        } catch (error) {
            console.error("최종계약서 다운로드 중 오류:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "최종계약서 처리 실패",
            }));
        } finally {
            setFormData((prev) => ({ ...prev, pdfMode: false, isLoading: false }));
        }
    };

    useEffect(() => {
        if (formData.contract) {
            console.log(formData.contract);
        }
    }, [formData]);

    return (
        <>
            {/* 토스트 메시지 */}
            {formData?.toastMsg && <Toast visible={true} msg={formData?.toastMsg} setFormData={setFormData} />}

            {/* Confirm 팝업 */}
            {formData?.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData?.confirmStatus.confirmTitle}
                    description={formData?.confirmStatus.confirmDescription}
                    buttons={formData?.confirmStatus.confirmButtons}
                />
            )}

            {/* 로딩 스피너 */}

            {signLinkGuidePopupStatus && (
                <SignLinkGuidePopup
                    popupStatus={signLinkGuidePopupStatus}
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, signLinkGuidePopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    sendContract={sendContract}
                    contractGroupId={contractGroupId}
                    contractId={contractId}
                />
            )}

            <ContractOptions
                formData={formData}
                handleFinalContractDownload={handleFinalContractDownload}
                handleSendContractPopup={handleSendContractPopup}
                handlePdfDownload={handlePdfDownload}
            />

            <div id="contract" ref={reportRef} className={`${formData?.pdfMode ? "pdf-mode" : ""}`}>
                <div className="contract-wrapper">
                    {/* Page 1 */}
                    <div className="page page_1">
                        <div className="page-head">
                            <div className="contract_num">계약서 번호 {formData?.contract?.serialNumber || ""}</div>
                            <h1>자동차 렌탈(대여) 계약서</h1>
                        </div>
                        <div className="page-content">
                            <div className="table-container">
                                <div className="table-wrapper">
                                    <table>
                                        <colgroup>
                                            <col width={"20%"} />
                                            <col width={"30%"} />
                                            <col width={"20%"} />
                                            <col width={"30%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>차량정보</th>
                                                <td>
                                                    {/* {formData?.contract?.contractCar.manufacturerName}&nbsp; */}
                                                    {formData?.contract?.contractCar?.modelName}&nbsp;
                                                    {/* {formData?.contract?.contractCar.modelDetailName}&nbsp; */}
                                                    {formData?.contract?.contractCar?.className}
                                                </td>
                                                <th>대여기간</th>
                                                <td>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.releasedAt, "YYYY-MM-DD HH:mm")} ~{" "}
                                                    <br />
                                                    {dateFormatDateTime(formData?.contract?.allocation?.returnedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>고객명</th>
                                                <td>{formData?.contract?.contractGroup?.customerName || ""}</td>
                                                <th>대여일</th>
                                                <td>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.releasedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>연락처</th>
                                                <td>{formData?.contract?.contractGroup?.customerPhone || ""}</td>
                                                <th>반납일</th>
                                                <td>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.returnedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="table-wrapper">
                                    <table>
                                        <colgroup>
                                            <col width={"20%"} />
                                            <col width={"30%"} />
                                            <col width={"20%"} />
                                            <col width={"30%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>렌트회사</th>
                                                <td>{formData?.contract?.rentCorp?.corpName || ""}</td>
                                                <th>대표자</th>
                                                <td>{formData?.contract?.rentCorp?.repName || ""}</td>
                                            </tr>
                                            <tr>
                                                <th>사업자번호</th>
                                                <td>{formData?.contract?.rentCorp?.corpNumber || ""}</td>
                                                <th>대표번호</th>
                                                <td>{formData?.contract?.rentCorp?.phone || ""}</td>
                                            </tr>
                                            <tr>
                                                <th>주소</th>
                                                <td colSpan={3}>
                                                    {formData?.contract?.rentCorp?.address || ""}&nbsp;
                                                    {formData?.contract?.rentCorp?.addressDetail || ""}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="corp_name">{formData?.contract?.rentCorp?.corpName || "렌터카 업체명"}</div>
                        </div>
                    </div>

                    {/* Page 2 */}
                    <div className="page page_2">
                        <div className="page-content">
                            <div className="table-container">
                                {/* ■ 고객기본사항 */}
                                <div className="table-wrapper">
                                    <div className="caption">■ 고객기본사항</div>
                                    <table>
                                        <colgroup>
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                        </colgroup>
                                        <tbody>
                                            {(() => {
                                                const mainDriver =
                                                    formData?.contract?.contractGroup?.contractDrivers?.find(
                                                        (driver) => driver.contractDriverType === "MAIN"
                                                    ) || {};

                                                return (
                                                    <>
                                                        <tr>
                                                            <th>성명</th>
                                                            <td>{mainDriver.name || ""}</td>
                                                            <th>주민번호</th>
                                                            <td>{mainDriver.idNumber || ""}</td>
                                                            <th>면허구분</th>
                                                            <td>
                                                                {(() => {
                                                                    // 면허 구분 매핑
                                                                    const licenseLabel =
                                                                        driverTypeOptions.find(
                                                                            (option) => option.value === mainDriver?.driverLicenseType
                                                                        )?.label || "";

                                                                    return licenseLabel;
                                                                })()}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>연락처</th>
                                                            <td>{mainDriver.phone || ""}</td>
                                                            <th>면허번호</th>
                                                            <td>{mainDriver.driverLicenseNumber || ""}</td>
                                                            <th>면허만기일</th>
                                                            <td>{mainDriver.issueDate || ""}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>주소</th>
                                                            <td colSpan={5} align="left">
                                                                {mainDriver.address || ""}&nbsp;
                                                                {mainDriver.addressDetail || ""}
                                                            </td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ■ 제 2 운전자 정보 */}
                                <div className="table-wrapper">
                                    <div className="caption">■ 제 2 운전자 정보</div>
                                    <table>
                                        <colgroup>
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                        </colgroup>
                                        <tbody>
                                            {(() => {
                                                const additionalDriver =
                                                    formData?.contract?.contractGroup?.contractDrivers?.find(
                                                        (driver) => driver.contractDriverType === "ADDITIONAL"
                                                    ) || {};

                                                return (
                                                    <>
                                                        <tr>
                                                            <th>성명</th>
                                                            <td>{additionalDriver.name || ""}</td>
                                                            <th>주민번호</th>
                                                            <td>{additionalDriver.idNumber || ""}</td>
                                                            <th>면허구분</th>
                                                            <td>
                                                                {(() => {
                                                                    // 면허 구분 매핑
                                                                    const licenseLabel =
                                                                        driverTypeOptions.find(
                                                                            (option) => option.value === additionalDriver?.driverLicenseType
                                                                        )?.label || "";

                                                                    return licenseLabel;
                                                                })()}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>연락처</th>
                                                            <td>{additionalDriver.phone || ""}</td>
                                                            <th>면허번호</th>
                                                            <td>{additionalDriver.driverLicenseNumber || ""}</td>
                                                            <th>면허만기일</th>
                                                            <td>{additionalDriver.issueDate || ""}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>주소</th>
                                                            <td colSpan={5}>
                                                                {additionalDriver.address || ""}&nbsp;
                                                                {additionalDriver.addressDetail || ""}
                                                            </td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ■ 법인 정보 */}
                                <div className="table-wrapper">
                                    <div className="caption">■ 법인 정보</div>
                                    <table>
                                        <colgroup>
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                            <col width={"15.4%"} />
                                            <col width={"18%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>법인명</th>
                                                <td>{formData?.contract?.contractGroup?.contractBusinessInfo?.corpName || ""}</td>
                                                <th>사업자번호</th>
                                                <td></td>
                                                <th>법인번호</th>
                                                <td>{formData?.contract?.contractGroup?.contractBusinessInfo?.bizNumber || ""}</td>
                                            </tr>
                                            <tr>
                                                <th>담당자</th>
                                                <td>{formData?.contract?.contractGroup?.contractBusinessInfo?.managerName || ""}</td>
                                                <th>연락처</th>
                                                <td>{formData?.contract?.contractGroup?.contractBusinessInfo?.managerPhone || ""}</td>
                                                <th>이메일</th>
                                                <td>{formData?.contract?.contractGroup?.contractBusinessInfo?.email || ""}</td>
                                            </tr>
                                            <tr>
                                                <th>주소</th>
                                                <td colSpan={5}>
                                                    {formData?.contract?.contractGroup?.contractBusinessInfo?.address || ""}&nbsp;
                                                    {formData?.contract?.contractGroup?.contractBusinessInfo?.addressDetail || ""}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* 1. 대여차량 정보 */}
                                <div className="table-wrapper">
                                    <div className="caption">1. 대여차량 정보</div>
                                    <table>
                                        <colgroup>
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th colSpan={2}>대여차종</th>
                                                <th>등급</th>
                                                <th>모델</th>
                                                <th colSpan={2}>상세모델</th>
                                                <th>색상</th>
                                                <th>연료</th>
                                            </tr>
                                            <tr>
                                                <th>차량번호</th>
                                                <td>{formData?.contract?.contractCar?.carNumber}</td>
                                                <td>{formData?.contract?.contractCar?.className}</td>
                                                <td>{formData?.contract?.contractCar?.modelName}</td>
                                                <td>{formData?.contract?.contractCar?.modelDetailName}</td>
                                                <td>{formData?.contract?.contractCar?.trimName}</td>
                                                <td>{formData?.contract?.contractCar?.color}</td>
                                                <td>{formData?.contract?.contractCar?.fuel}</td>
                                            </tr>
                                            <tr>
                                                <th>변속기</th>
                                                <td>{formData?.contract?.contractCar?.motorType}</td>
                                                <th>연식</th>
                                                <td>{dateFormatDateTime(formData?.contract?.contractCar?.firstDate, "YYYY")}</td>
                                                <th>배차주행거리</th>
                                                <td>{comma(formData?.contract?.contractCar?.mileage)}km</td>
                                                <th>배차유류량</th>
                                                <td>{formData?.contract?.contractCar?.gasCurrentAmount}%</td>
                                            </tr>
                                            <tr>
                                                <th>최초등록</th>
                                                <td>{formData?.contract?.contractCar?.firstDate}</td>
                                                <th>배기량</th>
                                                <td>{comma(formData?.contract?.contractCar?.displacement)}cc</td>
                                                <th>반차주행거리</th>
                                                <td>{comma(formData?.contract?.contractCar?.mileage)}km</td>
                                                <th>반차유류량</th>
                                                <td>{formData?.contract?.contractCar?.gasCurrentAmount}%</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* 2. 사고차량 정보 */}
                                <div className="table-wrapper">
                                    <div className="caption">2. 사고차량 정보</div>
                                    <table>
                                        <colgroup>
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                            <col width={"12.5%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>차량번호</th>
                                                <td>{formData?.contract?.contractGroup?.accidentCar?.carNumber}</td>
                                                <th>모델</th>
                                                <td>{formData?.contract?.contractGroup?.accidentCar?.modelName}</td>
                                                <th>상세모델</th>
                                                <td colSpan={3}>{formData?.contract?.contractGroup?.accidentCar?.modelDetailName}</td>
                                            </tr>
                                            <tr>
                                                <th>연식</th>
                                                <td>
                                                    {dateFormatDateTime(formData?.contract?.contractGroup?.accidentCar?.firstDate, "YYYY")}
                                                </td>
                                                <th>배기량</th>
                                                <td>{comma(formData?.contract?.contractGroup?.accidentCar?.displacement)}cc</td>
                                                <th>비고</th>
                                                <td colSpan={3}></td>
                                            </tr>
                                            <tr>
                                                <th>보험사</th>
                                                <td>{formData?.contract?.contractGroup?.contractInsurance?.insuranceCorpName}</td>
                                                <th>접수번호</th>
                                                <td>{formData?.contract?.contractGroup?.contractInsurance?.receiptNumber}</td>
                                                <th>담당자</th>
                                                <td>{formData?.contract?.contractGroup?.contractInsurance?.managerName}</td>
                                                <th>팩스번호</th>
                                                <td>{formData?.contract?.contractGroup?.contractInsurance?.faxNumber}</td>
                                            </tr>
                                            <tr>
                                                <th>공업사</th>
                                                <td>{formData?.contract?.contractGroup?.contractMechanic?.mechanicCorpName}</td>
                                                <th>연락처</th>
                                                <td>{formData?.contract?.contractGroup?.contractMechanic?.phone}</td>
                                                <th>주소</th>
                                                <td colSpan={3}>
                                                    {formData?.contract?.contractGroup?.contractMechanic?.address}&nbsp;
                                                    {formData?.contract?.contractGroup?.contractMechanic?.addressDetail}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>대여일시</th>
                                                <td colSpan={3}>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.releasedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                                <th>반납일시</th>
                                                <td colSpan={3}>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.returnedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>대여기간</th>
                                                <td colSpan={3}>
                                                    {dateFormatDateTime(formData?.contract?.allocation?.releasedAt, "YYYY-MM-DD HH:mm")} ~{" "}
                                                    <br />
                                                    {dateFormatDateTime(formData?.contract?.allocation?.returnedAt, "YYYY-MM-DD HH:mm")}
                                                </td>
                                                <th>대여요금</th>
                                                <td colSpan={3}></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="memo">
                                <p>
                                    임차인(계약자)는 본 계약의 주요 내용인 계약조건 세부사항, 자동차보험 사항, 정비서비스, 특약사항 및
                                    뒷면의 자동차 임대차 계약 약관에 대하여 충분한 설명을 듣고 잘 이해한 후 본인의 의사에 따라 본 계약을
                                    체결하였으며, 본 계약 내용을 성실히 이행할 것을 확약합니다.
                                </p>
                                <p>
                                    <em>※ 위 내용을 읽고 이해 하고 본 계약을 체결함을 동의 함.</em>
                                </p>
                            </div>

                            <div className="sign">
                                <div className="sign-box lessor_sign">
                                    <div className="signing-head">
                                        <span>임차인 </span>
                                    </div>
                                    <div className="signing-info">
                                        <div className="info-item">
                                            <span className="item-tit">성함</span>
                                            <span className="item-desc">{formData?.contract?.contractGroup?.customerName}</span>
                                        </div>
                                    </div>
                                    <div className="signing">
                                        <span className="sign-img">
                                            {formData?.contract?.signUpload?.objectUrl && (
                                                <img src={formData?.contract?.signUpload?.objectUrl} alt="" crossOrigin="anonymous" />
                                            )}
                                        </span>
                                        <span>(인)</span>
                                    </div>
                                </div>
                                <div className="sign-box tenant_sign">
                                    <div className="signing-head">
                                        <span>임대인</span>
                                    </div>
                                    <div className="signing-info">
                                        <div className="info-item">
                                            <span className="item-tit">업체명</span>
                                            <span className="item-desc">타봄렌트카</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="item-tit">연락처</span>
                                            <span className="item-desc">01012345678</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="item-tit">대표자</span>
                                            <span className="item-desc">박 정 근</span>
                                        </div>
                                    </div>
                                    <div className="signing">(인)</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 3 */}
                    <div className="page page_3">
                        <div className="page-content">
                            <div className="table-container">
                                <div className="table-wrapper">
                                    <div className="caption">3. 자동차보험 사항(자차손해면책 제도)</div>
                                    <table>
                                        <colgroup>
                                            <col width={"14%"} />
                                            <col width={"21.5%"} />
                                            <col width={"21.5%"} />
                                            <col width={"21.5%"} />
                                            <col width={"21.5%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>운전자범위</th>
                                                <td colSpan={2}>
                                                    {(() => {
                                                        const drivers = formData?.contract?.contractGroup?.contractDrivers || [];

                                                        // 가장 어린 운전자 나이 계산
                                                        const currentYear = new Date().getFullYear();
                                                        const minDriverAge = drivers.length
                                                            ? Math.min(
                                                                  ...drivers.map((driver) => {
                                                                      const birthYear = parseInt(driver.idNumber?.substring(0, 2), 10);
                                                                      const fullBirthYear =
                                                                          birthYear > 30 ? 1900 + birthYear : 2000 + birthYear;
                                                                      return currentYear - fullBirthYear;
                                                                  })
                                                              )
                                                            : null;

                                                        // 만 나이에 맞는 운전자 범위 찾기 (조건에 부합하는 value)
                                                        const closestAgeOption =
                                                            insuranceAgeOptions
                                                                .filter((option) => minDriverAge !== null && option.value <= minDriverAge)
                                                                .pop()?.label || "미확정";

                                                        return closestAgeOption;
                                                    })()}
                                                </td>
                                                <th>운전자연령</th>
                                                <td>
                                                    {(() => {
                                                        const drivers = formData?.contract?.contractGroup?.contractDrivers || [];

                                                        // 모든 운전자의 만 나이 계산 후 쉼표로 구분
                                                        const currentYear = new Date().getFullYear();
                                                        const driverAges = drivers
                                                            .map((driver) => {
                                                                if (!driver.idNumber) return null;
                                                                const birthYear = parseInt(driver.idNumber?.substring(0, 2), 10);
                                                                const fullBirthYear = birthYear > 30 ? 1900 + birthYear : 2000 + birthYear;
                                                                return `${currentYear - fullBirthYear}세`;
                                                            })
                                                            .join(", ");

                                                        return driverAges || "미확정";
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th rowSpan={9}>
                                                    가입내용
                                                    <br />
                                                    (보상한도 등)
                                                </th>
                                                <th>대인배상 I</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "DAEIN_PAY_1"
                                                        );
                                                        return daeinPay1Options.find((opt) => opt.value === item?.limitType)?.label || "";
                                                    })()}
                                                </td>
                                                <th>대인배상 II</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "DAEIN_PAY_2"
                                                        );
                                                        return (
                                                            daeinPay2Options.find((opt) => opt.value === item?.compensationRange)?.label ||
                                                            ""
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>대물배상</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "DAEMUL_PAY"
                                                        );
                                                        return (
                                                            daemulPayOptions.find((opt) => opt.value === item?.compensationRange)?.label ||
                                                            ""
                                                        );
                                                    })()}
                                                </td>
                                                <th>자손(사망)</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "JASON_DEAD"
                                                        );
                                                        return (
                                                            jasonOptions.find((opt) => opt.value === item?.compensationRange)?.label || ""
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>자손(부상)</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "JASON_INJURY"
                                                        );
                                                        return (
                                                            jasonOptions.find((opt) => opt.value === item?.compensationRange)?.label || ""
                                                        );
                                                    })()}
                                                </td>
                                                <th>자기차량손해</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "SELF_CAR_DAMAGE"
                                                        );
                                                        return (
                                                            selfCarDamageAmountOptions.find((opt) => opt.value === item?.compensationRange)
                                                                ?.label || ""
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>대인면책금(인당)</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "DAEIN_PAY_1"
                                                        );
                                                        return item?.selfPay ? `${item.selfPay.toLocaleString()}원` : "";
                                                    })()}
                                                </td>
                                                <th>대물면책금(건당)</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "DAEMUL_PAY"
                                                        );
                                                        return item?.selfPay ? `${item.selfPay.toLocaleString()}원` : "";
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>자손면책금</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find((i) =>
                                                            ["JASON_DEAD", "JASON_INJURY", "JASON_BLOCK"].includes(i.insuranceItemType)
                                                        );
                                                        return item?.selfPay ? `${item.selfPay.toLocaleString()}원` : "";
                                                    })()}
                                                </td>
                                                <th>자기차량면책금</th>
                                                <td>
                                                    {(() => {
                                                        const item = formData?.contract?.insuranceItems?.find(
                                                            (i) => i.insuranceItemType === "SELF_CAR_DAMAGE"
                                                        );
                                                        return item?.selfPay ? `${item.selfPay.toLocaleString()}원` : "";
                                                    })()}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>주의사항</th>
                                                <td colSpan={3}>
                                                    <em>교통 사고 접수시 경찰 신고 또는 보험사 현장 출동이 없는 사고는 사고 처리 불가</em>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>주의사항</th>
                                                <td colSpan={3}>
                                                    <em>교통 사고 접수시 경찰 신고 또는 보험사 현장 출동이 없는 사고는 사고 처리 불가</em>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <b>12대 중과실 사고</b>
                                                    <br />
                                                    ※ 자동차 임대차 계약
                                                    <br />
                                                    약관을 따름
                                                </th>
                                                <td colSpan={3} style={{ textAlign: "left" }}>
                                                    {`1. 신호위반       2. 중앙선침범       3. 속도위반(20Km이상 과속)        4. 앞지르기 방법위반
        5. 철길건널목 통과방법위반          6. 횡단보도사고        7. 무면허운전          8. 음주운전  
        9. 보도를 침범       10. 승객추락방지의무위반     11. 스쿨존 사고     12. 뺑소니 사고`}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>기타 주요사항</th>
                                                <td colSpan={3} style={{ textAlign: "left" }}>
                                                    {` 1. 렌트 계약자 및 지정운전자는 보험사 최근 사고내역 조회
        2 금연차량 이며, 흡연 또는 악취 발생 시 크리닝 비용 발생
        3. 1년 이내 사고 발생 시 임차인 과실비율 50% 이상의 사고 3회 누적 시 계약 해지`}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>보험사</th>
                                                <td>{formData?.contract?.contractGroup?.contractInsurance?.insuranceCorpName}</td>
                                                <th>보험사 대표번호</th>
                                                <td colSpan={2}>{formData?.contract?.contractGroup?.contractInsurance?.managerPhone}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="table-wrapper">
                                    <div className="caption">4. 특약 및 기타 안내사항</div>
                                    <table>
                                        <colgroup>
                                            <col width={"20%"} />
                                            <col width={"80%"} />
                                        </colgroup>
                                        <tbody>
                                            <tr>
                                                <th>특약사항</th>
                                                <td style={{ textAlign: "left" }}>{formData?.contract?.contractGroup?.memoForContract}</td>
                                            </tr>
                                            <tr>
                                                <th>과태료, 유류비</th>
                                                <td style={{ textAlign: "left" }}>
                                                    임차인은 임차기간동안 발생한 유류비 및 주정차 위반과 교통법규 위반등으로 인한 과태료와
                                                    범칙금 등은 임차인 부담입니다.
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>위치추적장치 장착</th>
                                                <td style={{ textAlign: "left" }}>
                                                    본 차량에는 위치정보를 수집할 수 있는 장치가 부착되어 있으며, 대여중에는 회사에서 정보
                                                    수집 및 이용을 할 수 있습니다. 이에 회사는 중요한 사항으로 임차인에게 고지하였으며,
                                                    임차인 본인은 위 정보수집 및 이용활동에 동의합니다.
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>대차서비스</th>
                                                <td style={{ textAlign: "left" }}>대차서비스는 지원이 불가 합니다.</td>
                                            </tr>
                                            <tr>
                                                <th>연락처, 주소 변경</th>
                                                <td style={{ textAlign: "left" }}>
                                                    정보 변경시 렌트사에 먼저 연락해야합니다. 연락 두절시 시동제어 및 계약해지 될 수
                                                    있습니다.
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>계약연장 및 해지</th>
                                                <td style={{ textAlign: "left" }}>
                                                    차량 연장시 시간당 하루요금의 10%씩 발생합니다. (유류는 환불불가 하며, 동일한 양으로
                                                    반납 바랍니다. 중도반납 시 환불 불가입니다.)
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="memo">
                                <p>
                                    임차인(계약자)는 본 계약의 주요 내용인 계약조건 세부사항, 자동차보험 사항, 정비서비스, 특약사항 및
                                    뒷면의 자동차 임대차 계약 약관에 대하여 충분한 설명을 듣고 잘 이해한 후 본인의 의사에 따라 본 계약을
                                    체결하였으며, 본 계약 내용을 성실히 이행할 것을 확약합니다.
                                </p>
                                <p>
                                    <em>※ 위 내용을 읽고 이해 하고 본 계약을 체결함을 동의 함.</em>
                                </p>
                            </div>

                            <div className="sign">
                                <div className="sign-box lessor_sign">
                                    <div className="signing-head">
                                        <span>임차인 </span>
                                    </div>
                                    <div className="signing-info">
                                        <div className="info-item">
                                            <span className="item-tit">성함</span>
                                            <span className="item-desc">{formData?.contract?.contractGroup?.customerName}</span>
                                        </div>
                                    </div>
                                    <div className="signing">
                                        <span className="sign-img">
                                            {formData?.contract?.signUpload?.objectUrl && (
                                                <img src={formData?.contract?.signUpload?.objectUrl} alt="" crossOrigin="anonymous" />
                                            )}
                                        </span>
                                        <span>(인)</span>
                                    </div>
                                </div>
                                <div className="sign-box tenant_sign">
                                    <div className="signing-head">
                                        <span>임대인</span>
                                    </div>
                                    <div className="signing-info">
                                        <div className="info-item">
                                            <span className="item-tit">업체명</span>
                                            <span className="item-desc">타봄렌트카</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="item-tit">연락처</span>
                                            <span className="item-desc">01012345678</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="item-tit">대표자</span>
                                            <span className="item-desc">박 정 근</span>
                                        </div>
                                    </div>
                                    <div className="signing">(인)</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const ContractOptions = ({ formData, handleFinalContractDownload, handleSendContractPopup, handlePdfDownload }) => {
    return ReactDOM.createPortal(
        <>
            <div className="contract-options">
                {formData?.contract?.contractStatus === "SIGN_FINISH" ? (
                    <button type="button" className="btn btn-secondary" onClick={handleFinalContractDownload}>
                        최종계약서 다운로드
                    </button>
                ) : (
                    <button type="button" className="btn btn-secondary" onClick={handleSendContractPopup}>
                        전자서명 발송
                    </button>
                )}
                <button type="button" className="btn btn-third" onClick={handlePdfDownload}>
                    PDF 다운로드
                </button>
            </div>
        </>,
        document.body
    );
};

// 서명 링크 발송 안내
const SignLinkGuidePopup = ({
    popupStatus,
    layer = true,
    onClose,
    formData,
    handleChange,
    setFormData,
    sendContract,
    contractGroupId,
    contractId,
}) => {
    const [uploadFile, { isLoading: isUploadLoading }] = useUploadFileMutation();

    const handleSend = async () => {
        try {
            setFormData((prev) => ({ ...prev, isLoading: true }));

            let originContractUploadId = null;

            // pdfFile이 세팅되어 있으면 -> 업로드
            if (formData.pdfFile) {
                const formDataObj = new FormData();
                formDataObj.append("file", formData.pdfFile);

                const uploadRes = await uploadFile(formDataObj).unwrap();
                originContractUploadId = uploadRes.data.id || null;
            }

            await sendContract({
                contractGroupId: contractGroupId,
                contractId: contractId,
                body: {
                    customerName: formData?.contract?.contractGroup?.customerName || "",
                    customerPhone: formData?.contract?.contractGroup?.customerPhone || "",
                    originContractUploadId,
                },
            }).unwrap();

            // 성공 시 Confirm 모달
            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "전자서명 발송 완료",
                    confirmDescription: "",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () =>
                                setFormData((p) => ({
                                    ...p,
                                    confirmStatus: { confirmPopupStatus: false },
                                })),
                        },
                    ],
                },
            }));
            onClose();
        } catch (error) {
            console.error("서명 링크 발송 실패:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "전자서명 발송 실패",
            }));
        } finally {
            setFormData((prev) => ({ ...prev, isLoading: false }));
        }
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    if (!popupStatus) return null;

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose} />
            <div className="popup confirm-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>서명 링크 발송 안내</span>
                    </div>
                    <div className="content-textbox">
                        <div className="item-list">
                            <div className="item">
                                <span className="item-tit">고객명</span>
                                <span className="item-input">
                                    <input
                                        type="text"
                                        placeholder=""
                                        name="contract.contractGroup.customerName"
                                        value={formData?.contract?.contractGroup?.customerName || ""}
                                        onChange={handleChange}
                                    />
                                </span>
                            </div>
                            <div className="item">
                                <span className="item-tit">연락처</span>
                                <span className="item-input">
                                    <input
                                        type="text"
                                        placeholder=""
                                        name="contract.contractGroup.customerPhone"
                                        value={formData?.contract?.contractGroup?.customerPhone || ""}
                                        onChange={handleChange}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="content-description">
                        <p>
                            서명 링크 발송 시 카카오톡 알림으로 전송되며, 카카오톡이 설치되어 있지 않은 경우 문자 메시지(LMS)로 전송됩니다.
                        </p>
                        <p>
                            서명자는 별도의 회원가입 없이 서명요청 받은 카카오톡 알림으로 접속해 인증 후 서명할 수 있습니다. (가급적 통화 후
                            서명 안내 부탁드립니다.)
                        </p>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-close" onClick={handleSend}>
                        발송
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
